import { useCallback, useState } from 'react'
import { useGetter } from './useGetter'
import { useResizeObserver } from './useResizeObserver'
import { useSyncedRef } from './useSyncedRef'

/**
 * Returns the width and height of the element to which `ref` is attached.
 * @returns ref, width, height, readOnlyRef
 */
export const useElementSize = () => {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [width, setWidth] = useState<number>(undefined)
    const [height, setHeight] = useState<number>(undefined)
    const getSize = useGetter({ width, height })
    const [node, setNode] = useState<HTMLElement | null>(null)
    /** read-only ref to the element being observed */
    const readOnlyRef = useSyncedRef(node)

    /**
     * __callback ref__: pass to the ref prop of the element you want to observe
     * - cannot read from `ref`
     * - use `readOnlyRef` for access to the underlying element
     */
    const ref = useCallback((node: HTMLElement | null) => {
        if (node) {
            const { width, height } = node.getBoundingClientRect()
            setWidth(width)
            setHeight(height)
        } else {
            setWidth(undefined)
            setHeight(undefined)
        }
        setNode(node)
    }, [])

    useResizeObserver(node, (entry) => {
        if (!entry) return
        // Firefox implements `contentBoxSize` as a single content rect, rather than an array
        const contentBoxSize: ResizeObserverSize = Array.isArray(
            entry.contentBoxSize,
        )
            ? (entry.contentBoxSize as ResizeObserverSize[])[0]
            : (entry.contentBoxSize as unknown as ResizeObserverSize)
        const boxSize = !contentBoxSize
            ? entry.contentRect
            : {
                  width: contentBoxSize.inlineSize,
                  height: contentBoxSize.blockSize,
              }
        if (boxSize.width !== getSize().width) {
            setWidth(boxSize.width)
        }
        if (boxSize.height !== getSize().height) {
            setHeight(boxSize.height)
        }
    })

    return {
        /**
         * __callback ref__: pass to the ref prop of the element you want to observe
         * - cannot read from `ref`
         * - use `readOnlyRef` for access to the underlying element
         */
        ref,
        width,
        height,
        /** read-only ref to the element being observed */
        readOnlyRef,
    }
}
